import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Gift, MapPin, ShieldCheck, MessageSquare, Star, Crown, ArrowRight, Recycle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import Showcase from "@/components/Showcase";

const HERO = "https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwyfHxwZW9wbGUlMjBoYXBweSUyMHRvZ2V0aGVyJTIwY29tbXVuaXR5fGVufDB8fHx8MTc4NDk5MjIyOHww&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: MapPin, title: "Nella tua zona", text: "Trova oggetti gratis vicino a te. Posizione approssimativa, indirizzo mai pubblico." },
  { icon: ShieldCheck, title: "Community sicura", text: "Profili verificati, recensioni e moderazione degli annunci per donare in fiducia." },
  { icon: MessageSquare, title: "Chat integrata", text: "Accordati direttamente con donatore o richiedente per il ritiro." },
  { icon: Crown, title: "Premium & Crediti", text: "Guadagna crediti donando e sblocca vantaggi con l'abbonamento Premium." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 glass border-b border-border/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center"><Gift className="h-5 w-5 text-primary-foreground" /></div>
            <span className="font-head font-extrabold text-xl tracking-tight text-primary">Ti Regalo</span>
          </div>
          <div className="flex items-center gap-2">
            {user ? (
              <Button onClick={() => navigate("/app")} data-testid="go-app-btn" className="rounded-full">Vai alla piattaforma</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate("/login")} data-testid="header-login-btn" className="rounded-full">Accedi</Button>
                <Button onClick={() => navigate("/register")} data-testid="header-register-btn" className="rounded-full">Iscriviti</Button>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-primary">
            <Recycle className="h-3.5 w-3.5" /> Regala. Ricevi. Riusa.
          </div>
          <h1 className="font-head font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
            Dai nuova vita agli oggetti che <span className="text-accent">non usi più</span>.
          </h1>
          <p className="text-lg text-muted-foreground mt-5 max-w-md">
            La community italiana per regalare e ricevere oggetti gratis nella tua zona. Semplice, sicura e a impatto zero.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button size="lg" onClick={() => navigate(user ? "/app" : "/register")} data-testid="hero-cta-btn" className="rounded-full text-base h-12 px-7">
              Inizia a donare <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate(user ? "/app" : "/login")} data-testid="hero-explore-btn" className="rounded-full text-base h-12 px-7">
              Esplora gli annunci
            </Button>
          </div>
          <div className="flex items-center gap-6 mt-10 text-sm text-muted-foreground">
            <div className="flex items-center gap-2"><Star className="h-4 w-4 text-[hsl(var(--gold))] fill-[hsl(var(--gold))]" /> Recensioni affidabili</div>
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /> Annunci moderati</div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.15 }} className="relative">
          <img src={HERO} alt="Community" className="rounded-[2rem] w-full aspect-[4/5] object-cover tactile-shadow" />
          <div className="absolute -bottom-5 -left-5 glass rounded-2xl p-4 tactile-shadow hidden sm:block">
            <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Guadagni</div>
            <div className="flex items-center gap-2 mt-1"><Crown className="h-5 w-5 text-[hsl(var(--gold))]" /><span className="font-head font-extrabold text-2xl">+10 crediti</span></div>
            <div className="text-xs text-muted-foreground">per ogni oggetto donato</div>
          </div>
        </motion.div>
      </section>

      <Showcase />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="rounded-2xl bg-card border border-border/60 p-6 tactile-shadow">
              <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center"><f.icon className="h-5 w-5 text-primary" /></div>
              <h3 className="font-head font-bold text-lg mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{f.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-[2rem] bg-primary text-primary-foreground p-10 sm:p-14 text-center relative overflow-hidden">
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl">Unisciti alla community di chi condivide</h2>
          <p className="text-primary-foreground/80 mt-3 max-w-xl mx-auto">Migliaia di oggetti aspettano una seconda vita. Regala ciò che non usi, trova ciò che ti serve.</p>
          <Button size="lg" variant="secondary" onClick={() => navigate(user ? "/app" : "/register")} data-testid="footer-cta-btn" className="rounded-full mt-8 h-12 px-8 text-base">
            Crea il tuo profilo gratis
          </Button>
        </div>
      </section>

      <footer className="border-t border-border/60 py-8 text-center text-sm text-muted-foreground">
        © 2026 Ti Regalo — ti regalo, se vieni a prenderlo.
      </footer>
    </div>
  );
}
